import React, { useEffect, useState, useCallback } from 'react';
import clsx from 'clsx';
import { EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { Product } from '../../types/product';
import { formatNumbers } from '../../utils/convert-to-arabic-price';
import RIYALIcon from '../../assets/riyal.svg?react';
import { getFrequentlyBoughtTogetherProducts } from '../../api/getFbt';
import { ENTERED_FROM_FBT } from '../../constants/tracking';
import {
  setTrackingSource,
  setDirectTrackingFlag,
} from '../../utils/tracking-session';
import { safeTrack } from '../../utils/analytics';
import { addToCartTracking } from '../../utils/cart-tracking';
import styles from './BundleOffer.module.css';

interface BundleOfferProps {
  currency?: string;
  locale?: 'ar-SA' | 'en-US';
  primaryColor?: string;
  store: string;
  provider: string;
  product?: string;
  customer?: string;
  skipInitialLoading?: boolean;
  preloadedProducts?: Product[];
  // Promotional banner props
  promotionTitle?: {
    ar: string;
    en: string;
  };
  promotionDescription?: {
    ar: string;
    en: string;
  };
  // Carousel options
  carouselOptions?: EmblaOptionsType;
  language?: string;
  fptTitle?: string;
  fptTitleEn?: string;
}

const BundleOffer: React.FC<BundleOfferProps> = ({
  currency = 'SAR',
  primaryColor = '#000000',
  store,
  provider,
  product,
  customer,
  skipInitialLoading = false,
  preloadedProducts,
  language = 'ar',
  fptTitle = 'عادة ما يتم شراؤها معًا',
  fptTitleEn = 'Frequently bought together',
  promotionTitle = {
    ar: fptTitle || 'عادة ما يتم شراؤها معًا',
    en: fptTitleEn || 'Frequently bought together',
  },
  // promotionDescription = {
  //   ar: 'يتم تطبيق العرض عند إضافة أي 3 عطور من اختيارك في السلة .',
  //   en: 'The offer is applied when you add any 3 perfumes of your choice to the cart.',
  // },
  carouselOptions = {
    align: 'start',
    direction: language === 'ar' ? 'rtl' : 'ltr',
    slidesToScroll: 1,
    containScroll: 'trimSnaps',
  },
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(
    new Set(),
  );
  const [isLoading, setIsLoading] = useState(!skipInitialLoading);
  // State for storing product variants for Zid products
  const [productVariants, setProductVariants] = useState<Record<number, any[]>>(
    {},
  );
  // State for storing selected variant for each product
  const [selectedVariants, setSelectedVariants] = useState<Record<number, any>>(
    {},
  );

  // Embla carousel setup
  const [emblaRef, emblaApi] = useEmblaCarousel(carouselOptions);
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi: any) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('reInit', onSelect);
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  // Reinitialize carousel when products change
  useEffect(() => {
    if (emblaApi && products.length > 0) {
      // Use setTimeout to ensure DOM has updated
      const timer = setTimeout(() => {
        emblaApi.reInit();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [emblaApi, products.length]);

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        // If preloaded products are provided, use them instead of making API call
        if (preloadedProducts && preloadedProducts.length > 0) {
          // Exclude the first product (assumed to be the main product)
          const bundleProducts = preloadedProducts.slice(1);
          setProducts(bundleProducts);
          // Don't select any products by default
          setSelectedProducts(new Set());

          // Fetch variants for Zid products even with preloaded products
          if (provider === 'zid' && window.zid && bundleProducts.length > 0) {
            try {
              const variantPromises = bundleProducts.map(async (prod) => {
                let productDetails;
                try {
                  const has_vitrin = window.vitrin == true;
                  if (has_vitrin) {
                    productDetails = await window.zid?.products.get(
                      String(prod.productId),
                    );
                  } else {
                    productDetails = await window.zid?.store.product.fetch(
                      String(prod.productId),
                    );
                  }
                  return {
                    productId: prod.id,
                    variants: productDetails.data.product.variants || [],
                  };
                } catch (error) {
                  console.error(
                    `Error fetching variants for product ${prod.productId}:`,
                    error,
                  );
                  return {
                    productId: prod.id,
                    variants: [],
                  };
                }
              });

              const variantResults = await Promise.all(variantPromises);
              const variantsMap: Record<number, any[]> = {};
              const selectedVariantsMap: Record<number, any> = {};

              variantResults.forEach(({ productId, variants }) => {
                if (variants.length > 0) {
                  variantsMap[productId] = variants;
                  // Select the first variant by default
                  selectedVariantsMap[productId] = variants[0];
                }
              });

              setProductVariants(variantsMap);
              setSelectedVariants(selectedVariantsMap);
            } catch (error) {
              console.error('Error fetching product variants:', error);
            }
          }

          setIsLoading(false);
          return;
        }

        // Use mock data based on environment
        const useMockData = false;

        if (useMockData) {
          // Simulate API delay
          await new Promise((resolve) => setTimeout(resolve, 1000));
          // Mock products would go here - for now using API
          setIsLoading(false);
        } else {
          const fetchedProducts = await getFrequentlyBoughtTogetherProducts({
            storeId: store,
            storeType: provider,
            productId: product || undefined,
            currency,
          });
          // Exclude the first product (assumed to be the main product)
          const bundleProducts = fetchedProducts.slice(1);
          setProducts(bundleProducts);
          // Don't select any products by default
          setSelectedProducts(new Set());

          // Fetch variants for Zid products in parallel
          if (provider === 'zid' && window.zid && bundleProducts.length > 0) {
            try {
              const variantPromises = bundleProducts.map(async (prod) => {
                let productDetails;
                try {
                  const has_vitrin = window.vitrin == true;
                  if (has_vitrin) {
                    productDetails = await window.zid?.products.get(
                      String(prod.productId),
                    );
                  } else {
                    productDetails = await window.zid?.store.product.fetch(
                      String(prod.productId),
                    );
                  }
                  return {
                    productId: prod.id,
                    variants: productDetails.data.product.variants || [],
                  };
                } catch (error) {
                  console.error(
                    `Error fetching variants for product ${prod.productId}:`,
                    error,
                  );
                  return {
                    productId: prod.id,
                    variants: [],
                  };
                }
              });

              const variantResults = await Promise.all(variantPromises);
              const variantsMap: Record<number, any[]> = {};
              const selectedVariantsMap: Record<number, any> = {};

              variantResults.forEach(({ productId, variants }) => {
                if (variants.length > 0) {
                  variantsMap[productId] = variants;
                  // Select the first variant by default
                  selectedVariantsMap[productId] = variants[0];
                }
              });

              setProductVariants(variantsMap);
              setSelectedVariants(selectedVariantsMap);
            } catch (error) {
              console.error('Error fetching product variants:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching bundle products:', error);
        setProducts([]);
        setSelectedProducts(new Set());
      } finally {
        setIsLoading(false);
      }
    };

    if (preloadedProducts || (store && provider)) {
      fetchProducts();
    }
  }, [store, provider, product, currency, preloadedProducts]);

  // Handle product selection
  const toggleProductSelection = (productId: number) => {
    // Find the product to check if it's available
    const product = products.find((p) => p.id === productId);

    // Don't allow toggling unavailable products
    if (!product || !product.isAvailable) {
      return;
    }

    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  // Handle variant selection for a product
  const handleVariantSelection = (productId: number, variant: any) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [productId]: variant,
    }));
  };

  // Calculate total price using discounted price if available (only for selected products)
  // const calculateTotal = () => {
  //   return products.reduce((total, product) => {
  //     if (selectedProducts.has(product.id)) {
  //       const price = product.discountedPrice || product.price;
  //       return total + price;
  //     }
  //     return total;
  //   }, 0);
  // };

  // const calculateOriginalTotal = () => {
  //   return products.reduce((total, product) => {
  //     if (selectedProducts.has(product.id)) {
  //       return total + product.price;
  //     }
  //     return total;
  //   }, 0);
  // };

  // const total = calculateTotal();
  // const originalTotal = calculateOriginalTotal();
  // const hasDiscount = total < originalTotal;
  // const selectedProductsArray = products.filter((p) =>
  //   selectedProducts.has(p.id),
  // );

  const onProductClick = async (product: Product) => {
    window.open(product.url, '_self');

    setTrackingSource(ENTERED_FROM_FBT, product.productId.toString());

    safeTrack('product_clicked', {
      storeId: store,
      provider,
      customerId: customer,
      product: product.productId,
      productName: product.name.ar,
      type: 'bundle_offer',
    });
  };

  const quickAddToCart = async (products: Product[], quantity: number) => {
    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      safeTrack('product_added_to_cart', {
        storeId: store,
        provider,
        customerId: customer,
        product: product.productId,
        productName: product.name.ar,
        type: 'fbt',
        reached_from: 'frequently_bought_together',
      });
      setDirectTrackingFlag(); // Set flag BEFORE API call to prevent ProductView duplicate
      addToCartTracking(product.name.ar, 'frequently_bought_together');
      await window.salla?.api.cart.quickAdd(product.productId, quantity);

      // Add delay after adding product, except for the last one
      if (i < products.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 100)); // 120ms delay
      }
    }
  };

  const quickAddToZidCart = async (products: Product[]) => {
    products.forEach(async (product) => {
      if (!window.zid) return;

      const hasVariants =
        productVariants[product.id] && productVariants[product.id].length > 0;
      const selectedVariant = selectedVariants[product.id];

      if (hasVariants && !selectedVariant) {
        console.error(`No variant selected for product ${product.productId}`);
        return;
      }

      setDirectTrackingFlag();
      addToCartTracking(product.name.ar, 'frequently_bought_together');

      try {
        const has_vitrin = window.vitrin == true;
        const product_id = hasVariants ? selectedVariant.id : product.productId;
        const addtoCartResponse = has_vitrin
          ? await window.zid.cart.addProduct({
              product_id: product_id,
              quantity: 1,
            })
          : await window.zid.store.cart.addProduct({
              productId: product_id,
              quantity: 1,
            });

        const addtoCartRes = await addtoCartResponse.json();
        if (addtoCartRes.status === 'success') {
          safeTrack('product_added_to_cart', {
            storeId: store,
            provider,
            customerId: customer,
            product: product.productId,
            productName: product.name.ar,
            type: 'bundle_offer',
          });
        }
      } catch (error) {
        console.error('Error adding product to cart:', error);
      }
    });
  };

  const quickAddToWoocommerceCart = async (
    products: Product[],
    quantity: number,
  ) => {
    products.forEach(async (product) => {
      setDirectTrackingFlag();
      addToCartTracking(product.name.ar, 'frequently_bought_together');
      try {
        const response = await fetch('/?wc-ajax=add_to_cart', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: `product_id=${product.productId}&quantity=${quantity}`,
        });

        const data = await response.json();

        if (data.error) {
          console.error('Error adding to cart:', data.message);
        } else {
          safeTrack('product_added_to_cart', {
            storeId: store,
            provider,
            customerId: customer,
            product: product.productId,
            productName: product.name.ar,
            type: 'bundle_offer',
          });
          console.log('Product added to cart:', data);

          document.dispatchEvent(
            new CustomEvent('added_to_cart', { detail: product.productId }),
          );

          const cartFragmentResponse = await fetch(
            '/?wc-ajax=get_refreshed_fragments',
          );
          const cartFragmentData = await cartFragmentResponse.json();

          if (cartFragmentData && cartFragmentData.fragments) {
            for (const [selector, html] of Object.entries(
              cartFragmentData.fragments,
            )) {
              const element = document.querySelector(selector);
              if (element) {
                element.innerHTML = html as string;
              }
            }
          }
        }
      } catch (error) {
        console.error('Request failed:', error);
      }
    });
  };

  const onAddAllToCart = useCallback(
    (products: Product[]) => {
      console.log('products', products);
      provider === 'salla'
        ? quickAddToCart(products, 1)
        : provider === 'zid'
        ? quickAddToZidCart(products)
        : quickAddToWoocommerceCart(products, 1);
    },
    [provider],
  );

  // Listen for Salla quick-buy button clicks
  useEffect(() => {
    const handleQuickBuyClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if the clicked element or its parent is the quick-buy button
      const quickBuyButton = target.closest(
        'button[quick-buy][product-id]',
      ) as HTMLButtonElement | null;

      if (quickBuyButton) {
        // Check if any products are selected
        if (selectedProducts.size > 0) {
          const selectedProductsArray = products.filter((p) =>
            selectedProducts.has(p.id),
          );
          if (selectedProductsArray.length > 0) {
            // Add a delay before adding to cart (e.g., 300ms)
            setTimeout(() => {
              onAddAllToCart(selectedProductsArray);
            }, 300);
          }
        }
      }
    };

    // Add event listener to document for event delegation
    document.addEventListener('click', handleQuickBuyClick);

    return () => {
      document.removeEventListener('click', handleQuickBuyClick);
    };
  }, [selectedProducts, products, onAddAllToCart]);

  if (isLoading) {
    return (
      <div className={styles.bundleOffer}>
        <div className={styles.bundleOfferLoading}>
          <span className={styles.loaderBundle}></span>
        </div>
      </div>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <div
      className={styles.bundleOffer}
      style={
        {
          '--bundle-primary-color': primaryColor,
          '--bundle-primary-color-light': `${primaryColor}0d`,
        } as React.CSSProperties
      }
    >
      {/* Promotional Banner */}
      <div className={styles.promotionalBanner}>
        <h3 className={styles.promotionTitle}>
          {language === 'ar' ? promotionTitle.ar : promotionTitle.en}
        </h3>
        {/* <p className={styles.promotionDescription}>
          {locale === 'ar-SA'
            ? promotionDescription.ar
            : promotionDescription.en}
        </p> */}
      </div>

      {/* Products Carousel */}
      <div className={styles.carouselWrapper}>
        <div className={styles.carouselContainer}>
          <button
            className={clsx(styles.carouselButton, styles.carouselButtonPrev)}
            onClick={scrollPrev}
            disabled={prevBtnDisabled}
            aria-label="Previous products"
          >
            <svg viewBox="0 0 532 532" className={styles.carouselButtonIcon}>
              <path
                fill="currentColor"
                d="M176.34 520.646c-13.793 13.805-36.208 13.805-50.001 0-13.785-13.804-13.785-36.238 0-50.034L330.78 266 126.34 61.391c-13.785-13.805-13.785-36.239 0-50.044 13.793-13.796 36.208-13.796 50.002 0 22.928 22.947 206.395 206.507 229.332 229.454a35.065 35.065 0 0 1 10.326 25.126c0 9.2-3.393 18.26-10.326 25.2-45.865 45.901-206.404 206.564-229.332 229.52Z"
              />
            </svg>
          </button>

          <div
            className={styles.emblaViewport}
            ref={emblaRef}
            dir={language === 'ar' ? 'rtl' : 'ltr'}
          >
            <div className={styles.emblaContainer}>
              {products.map((product) => (
                <div key={product.id} className={styles.emblaSlide}>
                  <div
                    className={clsx(
                      styles.bundleProduct,
                      !product.isAvailable && styles.outOfStock,
                    )}
                  >
                    <div className={styles.productCheckbox}>
                      <button
                        type="button"
                        className={clsx(
                          styles.checkboxButton,
                          selectedProducts.has(product.id) && styles.checked,
                        )}
                        disabled={!product.isAvailable}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleProductSelection(product.id);
                        }}
                        aria-label={`Select ${
                          product.name.ar || product.name.en
                        }`}
                      >
                        {selectedProducts.has(product.id) && (
                          <span className={styles.checkmark}>✓</span>
                        )}
                      </button>
                    </div>

                    <div
                      className={styles.productImage}
                      onClick={() => onProductClick(product)}
                    >
                      <img
                        src={product.mainImage}
                        alt={
                          language === 'ar'
                            ? product.name.ar
                            : product.name.en || ''
                        }
                      />
                    </div>

                    <div className={styles.productInfo}>
                      <h4
                        className={styles.productName}
                        onClick={() => onProductClick(product)}
                      >
                        {language === 'ar'
                          ? product.name.ar
                          : product.name.en || ''}
                      </h4>

                      <div className={styles.productPrice}>
                        {!product.isAvailable ? (
                          <div className={styles.outOfStockText}>
                            <span>
                              {language === 'ar' ? 'غير متوفر' : 'Out of Stock'}
                            </span>
                          </div>
                        ) : product.discountedPrice ? (
                          <div className={styles.priceContainer}>
                            <div className={styles.currentPrice}>
                              <span className={styles.priceValue}>
                                {formatNumbers(
                                  product.discountedPrice,
                                  'en-US',
                                  currency,
                                )}
                              </span>
                              {currency === 'SAR' && (
                                <RIYALIcon className={styles.riyalIcon} />
                              )}
                            </div>
                            <div className={styles.originalPrice}>
                              <span className={styles.priceValue}>
                                {formatNumbers(
                                  product.price,
                                  'en-US',
                                  currency,
                                )}
                              </span>
                              {currency === 'SAR' && (
                                <RIYALIcon className={styles.riyalIcon} />
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className={styles.currentPrice}>
                            <span className={styles.priceValue}>
                              {formatNumbers(product.price, 'en-US', currency)}
                            </span>
                            {currency === 'SAR' && (
                              <RIYALIcon className={styles.riyalIcon} />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Variant selection for Zid products */}
                      {provider === 'zid' &&
                        productVariants[product.id] &&
                        productVariants[product.id].length > 0 && (
                          <div className={styles.bundleVariants}>
                            <select
                              className={styles.variantSelect}
                              value={selectedVariants[product.id]?.id || ''}
                              onChange={(e) => {
                                const selectedVariant = productVariants[
                                  product.id
                                ].find(
                                  (variant) => variant.id === e.target.value,
                                );
                                if (selectedVariant) {
                                  handleVariantSelection(
                                    product.id,
                                    selectedVariant,
                                  );
                                }
                              }}
                            >
                              {productVariants[product.id].map((variant) => {
                                const shortVariantName =
                                  variant.attributes?.find(
                                    (attr: any) => attr.name === 'النوع',
                                  )?.value;

                                const displayName =
                                  shortVariantName ||
                                  (variant.name.length > 30
                                    ? `${variant.name.substring(0, 30)}...`
                                    : variant.name);

                                return (
                                  <option
                                    key={variant.id}
                                    value={variant.id}
                                    title={variant.name}
                                  >
                                    {displayName}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            className={clsx(styles.carouselButton, styles.carouselButtonNext)}
            onClick={scrollNext}
            disabled={nextBtnDisabled}
            aria-label="Next products"
          >
            <svg viewBox="0 0 532 532" className={styles.carouselButtonIcon}>
              <path
                fill="currentColor"
                d="M355.66 11.354c13.793-13.805 36.208-13.805 50.001 0 13.785 13.804 13.785 36.238 0 50.034L201.22 266l204.442 204.61c13.785 13.805 13.785 36.239 0 50.044-13.793 13.796-36.208 13.796-50.002 0a5994246.277 5994246.277 0 0 0-229.332-229.454 35.065 35.065 0 0 1-10.326-25.126c0-9.2 3.393-18.26 10.326-25.2C172.192 194.973 332.731 34.31 355.66 11.354Z"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Total and Add to Cart */}
      {/* <div className={styles.bundleTotal}>
        <div className={styles.bundleTotalLabel}>المجموع</div>

        <div className={styles.bundleTotalPrice}>
          {hasDiscount ? (
            <div className={styles.priceContainer}>
              <div className={styles.currentPrice}>
                <span className={styles.priceValue}>
                  {formatNumbers(total, locale, currency)}
                </span>
                {currency === 'SAR' && (
                  <RIYALIcon className={styles.riyalIcon} />
                )}
              </div>
              <div className={styles.originalPrice}>
                <span className={styles.priceValue}>
                  {formatNumbers(originalTotal, locale, currency)}
                </span>
                {currency === 'SAR' && (
                  <RIYALIcon className={styles.riyalIcon} />
                )}
              </div>
            </div>
          ) : (
            <div className={styles.currentPrice}>
              <span className={styles.priceValue}>
                {formatNumbers(total, locale, currency)}
              </span>
              {currency === 'SAR' && <RIYALIcon className={styles.riyalIcon} />}
            </div>
          )}
        </div>

        <button
          className={styles.addAllBtn}
          onClick={() => onAddAllToCart(selectedProductsArray)}
          disabled={isLoading || selectedProducts.size === 0}
          style={{
            backgroundColor: primaryColor,
            borderColor: primaryColor,
            opacity: selectedProducts.size === 0 ? 0.5 : 1,
          }}
        >
          {isLoading ? (
            <span className={styles.loaderAddToCart}></span>
          ) : (
            `إضافة ${selectedProducts.size} منتج للسلة`
          )}
        </button>
      </div> */}
    </div>
  );
};

export default BundleOffer;
